import Vue from "vue";
import { v4 as uuidv4 } from "uuid";

export const DEFAULT_NODE_SIZE = { width: 620, height: 140 };
export const EDGE_BUS_GAP = 24;
export const PUSH_GAP = 120;

// Without an id, flattenMessageTree drops the node and its subtree (blank canvas).
export function normalizeIds(tree) {
  const roots = Array.isArray(tree) ? tree : [];
  const usedIds = new Set();
  // WeakSet keeps the pass idempotent when one node object is aliased into two branches.
  const seen = new WeakSet();

  // First occurrence keeps a duplicated id, matching playback's first-wins index.
  const assignIds = node => {
    if (!node || typeof node !== "object" || seen.has(node)) return;
    seen.add(node);
    if (!node.id || usedIds.has(node.id)) {
      // Vue.set — `id` is often a new property, invisible to Vue 2 via plain assignment.
      Vue.set(node, "id", uuidv4());
    }
    usedIds.add(node.id);
    if (Array.isArray(node.children)) node.children.forEach(assignIds);
  };
  roots.forEach(assignIds);
}

export function flattenMessageTree(messageTree) {
  const nodes = [];
  const edges = [];
  const seen = new Set();
  const walk = (node, parent, index) => {
    if (!node || typeof node !== "object" || !node.id) return;
    if (!seen.has(node.id)) {
      seen.add(node.id);
      nodes.push({ id: node.id, node, parent, index });
    }
    if (Array.isArray(node.children)) {
      node.children.forEach((child, i) => {
        if (child && child.id) edges.push({ from: node.id, to: child.id, type: "children" });
        walk(child, node, i);
      });
    }
    if (node.next) edges.push({ from: node.id, to: node.next, type: "next" });
  };
  (Array.isArray(messageTree) ? messageTree : []).forEach((root, i) => walk(root, null, i));
  return { nodes, edges };
}

export function findNodeById(tree, id) {
  let found = null;
  const walk = node => {
    if (found || !node || typeof node !== "object") return;
    if (node.id === id) {
      found = node;
      return;
    }
    if (Array.isArray(node.children)) node.children.forEach(walk);
  };
  (Array.isArray(tree) ? tree : []).forEach(walk);
  return found;
}

export function collectNodeIds(tree) {
  const ids = new Set();
  const collect = node => {
    if (!node || typeof node !== "object") return;
    if (node.id) ids.add(node.id);
    if (Array.isArray(node.children)) node.children.forEach(collect);
  };
  (Array.isArray(tree) ? tree : []).forEach(collect);
  return ids;
}

export function buildAdjacency(tree) {
  const adjacency = {};
  const build = node => {
    if (!node || typeof node !== "object") return;
    if (node.id) {
      const outs = [];
      if (Array.isArray(node.children)) {
        node.children.forEach(child => {
          if (child && child.id) outs.push(child.id);
        });
      }
      if (node.next) outs.push(node.next);
      adjacency[node.id] = outs;
    }
    if (Array.isArray(node.children)) node.children.forEach(build);
  };
  (Array.isArray(tree) ? tree : []).forEach(build);
  return adjacency;
}

// Predecessors of `targetId` (+ itself): linking a source to any of these would close a loop.
export function idsThatReach(tree, targetId) {
  const adjacency = buildAdjacency(tree);
  const reverse = {};
  Object.keys(adjacency).forEach(id => {
    adjacency[id].forEach(to => (reverse[to] = reverse[to] || []).push(id));
  });
  const result = new Set([targetId]);
  const stack = [targetId];
  while (stack.length) {
    const current = stack.pop();
    (reverse[current] || []).forEach(pred => {
      if (!result.has(pred)) {
        result.add(pred);
        stack.push(pred);
      }
    });
  }
  return result;
}

export function hasCycle(tree) {
  const adjacency = buildAdjacency(tree);
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = {};
  Object.keys(adjacency).forEach(id => {
    color[id] = WHITE;
  });
  let cycle = false;
  const dfs = id => {
    color[id] = GRAY;
    for (const next of adjacency[id] || []) {
      if (color[next] === GRAY) {
        cycle = true;
        return;
      }
      if (color[next] === WHITE) {
        dfs(next);
        if (cycle) return;
      }
    }
    color[id] = BLACK;
  };
  for (const id of Object.keys(adjacency)) {
    if (color[id] === WHITE) dfs(id);
    if (cycle) break;
  }
  return cycle;
}

export function findDanglingNext(tree) {
  const ids = collectNodeIds(tree);
  let dangling = null;
  const walk = node => {
    if (dangling || !node || typeof node !== "object") return;
    if (node.next && !ids.has(node.next)) {
      dangling = node.next;
      return;
    }
    if (Array.isArray(node.children)) node.children.forEach(walk);
  };
  (Array.isArray(tree) ? tree : []).forEach(walk);
  return dangling;
}

export function findDuplicateIds(tree) {
  const seen = new Set();
  const duplicates = new Set();
  // Same WeakSet guard as normalizeIds: one node object aliased into two branches is not a duplicate id.
  const visited = new WeakSet();
  const walk = node => {
    if (!node || typeof node !== "object" || visited.has(node)) return;
    visited.add(node);
    if (node.id) {
      if (seen.has(node.id)) duplicates.add(node.id);
      seen.add(node.id);
    }
    if (Array.isArray(node.children)) node.children.forEach(walk);
  };
  (Array.isArray(tree) ? tree : []).forEach(walk);
  return [...duplicates];
}

// Duplicates first — ambiguous ids make the other diagnostics unreliable.
export function flowErrorMessage(tree) {
  if (!Array.isArray(tree)) return 'The flow "messages" value is not a list of nodes.';
  const duplicates = findDuplicateIds(tree);
  if (duplicates.length) return `The flow has duplicated node ids (${duplicates.join(", ")}).`;
  const dangling = findDanglingNext(tree);
  if (dangling) return `A "next" reference points to a missing node id (${dangling}).`;
  if (hasCycle(tree)) return 'The flow contains a loop (a "next" reference points back into its own path).';
  return null;
}

export function edgeName(edge) {
  return `${edge.type}:${edge.from}->${edge.to}`;
}

// Orthogonal route via a bus above the target's port, so many edges into one target share a junction.
export function edgePath(coords) {
  const x1 = Math.round(coords.x1);
  const y1 = Math.round(coords.y1);
  const x2 = Math.round(coords.x2);
  const y2 = Math.round(coords.y2);
  const busY = y2 - EDGE_BUS_GAP;
  if (x1 === x2) return `M ${x1} ${y1} L ${x2} ${y2}`;
  return `M ${x1} ${y1} V ${busY} H ${x2} V ${y2}`;
}

export function makeEdge(edge, coords) {
  return { id: edgeName(edge), type: edge.type, from: edge.from, to: edge.to, d: edgePath(coords) };
}

export function buildEdges(graphEdges, portsOf) {
  const edges = [];
  graphEdges.forEach(edge => {
    const source = portsOf(edge.from);
    const target = portsOf(edge.to);
    if (!source || !target) return;
    edges.push(makeEdge(edge, { x1: source.cx, y1: source.bottom, x2: target.cx, y2: target.top }));
  });
  return edges;
}
